import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User, Tenant, Video, Grade } from '../types';

/* ------------------------------------------------------------------ */
/*  Auth persistence helpers                                           */
/* ------------------------------------------------------------------ */

const AUTH_STORAGE_KEY = 'vqs_auth';
const REMEMBER_ME_KEY = 'vqs_remember_me';

interface PersistedAuth {
  user: User;
  accessToken: string;
  refreshToken: string;
}

/**
 * Whether "Remember Me" is active.
 * Defaults to true if the key is absent (backward compat with existing sessions
 * that were persisted to localStorage before this flag existed).
 */
function isRememberMe(): boolean {
  const val = localStorage.getItem(REMEMBER_ME_KEY);
  return val === null ? true : val === 'true';
}

function loadPersistedAuth(): PersistedAuth | null {
  try {
    const storage = isRememberMe() ? localStorage : sessionStorage;
    const raw = storage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedAuth;
  } catch {
    return null;
  }
}

/**
 * Persist auth tokens.
 * @param rememberMe - when provided, updates the storage preference for this session.
 *                     when omitted, uses the current preference (e.g. on token refresh).
 */
function persistAuth(data: PersistedAuth, rememberMe?: boolean): void {
  const remember = rememberMe !== undefined ? rememberMe : isRememberMe();
  localStorage.setItem(REMEMBER_ME_KEY, String(remember));
  const storage = remember ? localStorage : sessionStorage;
  storage.setItem(AUTH_STORAGE_KEY, JSON.stringify(data));
  // Remove stale copy from the other storage
  const other = remember ? sessionStorage : localStorage;
  other.removeItem(AUTH_STORAGE_KEY);
}

function clearPersistedAuth(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  sessionStorage.removeItem(AUTH_STORAGE_KEY);
  localStorage.removeItem('vqs_tenant_id');
  localStorage.removeItem(REMEMBER_ME_KEY);
}

/* ------------------------------------------------------------------ */
/*  Auth slice                                                         */
/* ------------------------------------------------------------------ */

const restored = loadPersistedAuth();

interface AuthSlice {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  setAuth: (user: User, accessToken: string, refreshToken: string, rememberMe?: boolean) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthSlice>((set) => ({
  user: restored?.user ?? null,
  accessToken: restored?.accessToken ?? null,
  refreshToken: restored?.refreshToken ?? null,
  isAuthenticated: !!restored,
  setAuth: (user, accessToken, refreshToken, rememberMe) => {
    persistAuth({ user, accessToken, refreshToken }, rememberMe);
    set({ user, accessToken, refreshToken, isAuthenticated: true });
  },
  clearAuth: () => {
    clearPersistedAuth();
    set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false });
    useUploadStore.getState().clearAllUploads();
    useProcessingStagesStore.getState().clearAllProcessingStages();
  },
}));

/* ------------------------------------------------------------------ */
/*  Tenant slice                                                       */
/* ------------------------------------------------------------------ */

interface TenantSlice {
  tenant: Tenant | null;
  tenantId: string | null;
  setTenant: (tenant: Tenant) => void;
  clearTenant: () => void;
}

export const useTenantStore = create<TenantSlice>((set) => ({
  tenant: null,
  tenantId: null,
  setTenant: (tenant) => set({ tenant, tenantId: tenant.id }),
  clearTenant: () => set({ tenant: null, tenantId: null }),
}));

/* ------------------------------------------------------------------ */
/*  Video slice                                                        */
/* ------------------------------------------------------------------ */

interface VideoSlice {
  videos: Video[];
  selectedVideo: Video | null;
  totalVideos: number;
  gradeFilter: Grade | null;
  setVideos: (videos: Video[], total: number) => void;
  setSelectedVideo: (video: Video | null) => void;
  setGradeFilter: (grade: Grade | null) => void;
}

export const useVideoStore = create<VideoSlice>((set) => ({
  videos: [],
  selectedVideo: null,
  totalVideos: 0,
  gradeFilter: null,
  setVideos: (videos, total) => set({ videos, totalVideos: total }),
  setSelectedVideo: (video) => set({ selectedVideo: video }),
  setGradeFilter: (grade) => set({ gradeFilter: grade }),
}));

/* ------------------------------------------------------------------ */
/*  Processing stages slice                                            */
/* ------------------------------------------------------------------ */

interface ProcessingStagesSlice {
  /** Maps videoId → human-readable stage label (e.g. "Scanning for violations") */
  processingStages: Record<string, string>;
  setProcessingStage: (videoId: string, label: string) => void;
  clearProcessingStage: (videoId: string) => void;
  clearAllProcessingStages: () => void;
}

export const useProcessingStagesStore = create<ProcessingStagesSlice>()(
  persist(
    (set) => ({
      processingStages: {},
      setProcessingStage: (videoId, label) =>
        set((s) => ({ processingStages: { ...s.processingStages, [videoId]: label } })),
      clearProcessingStage: (videoId) =>
        set((s) => {
          const next = { ...s.processingStages };
          delete next[videoId];
          return { processingStages: next };
        }),
      clearAllProcessingStages: () => set({ processingStages: {} }),
    }),
    {
      name: 'vqs-processing-stages',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

/* ------------------------------------------------------------------ */
/*  Upload slice                                                       */
/* ------------------------------------------------------------------ */

type UploadStatus = 'uploading' | 'processing' | 'scored' | 'flagged' | 'failed';

export interface UploadItem {
  clientId: string;
  videoId: string | null;
  title: string;
  progress: number;
  status: UploadStatus;
  stageLabel: string;
}

interface UploadStoreSlice {
  uploads: UploadItem[];
  startUpload: (clientId: string, title: string) => void;
  setUploadProgress: (clientId: string, progress: number) => void;
  confirmUpload: (clientId: string, videoId: string) => void;
  failUpload: (clientId: string) => void;
  setStageLabel: (videoId: string, label: string) => void;
  updateVideoStatus: (videoId: string, status: 'scored' | 'flagged' | 'failed') => void;
  dismiss: (clientId: string) => void;
  dismissByVideoId: (videoId: string) => void;
  clearAllUploads: () => void;
}

export const useUploadStore = create<UploadStoreSlice>()(
  persist(
    (set) => ({
      uploads: [],
      startUpload: (clientId, title) =>
        set((s) => ({
          uploads: [...s.uploads, { clientId, videoId: null, title, progress: 0, status: 'uploading', stageLabel: '' }],
        })),
      setUploadProgress: (clientId, progress) =>
        set((s) => ({
          uploads: s.uploads.map((u) => u.clientId === clientId ? { ...u, progress } : u),
        })),
      confirmUpload: (clientId, videoId) =>
        set((s) => ({
          uploads: s.uploads.map((u) =>
            u.clientId === clientId ? { ...u, videoId, status: 'processing', stageLabel: 'Processing...' } : u,
          ),
        })),
      failUpload: (clientId) =>
        set((s) => ({
          uploads: s.uploads.map((u) =>
            u.clientId === clientId ? { ...u, status: 'failed', stageLabel: '' } : u,
          ),
        })),
      setStageLabel: (videoId, label) =>
        set((s) => ({
          uploads: s.uploads.map((u) => u.videoId === videoId ? { ...u, stageLabel: label } : u),
        })),
      updateVideoStatus: (videoId, status) =>
        set((s) => ({
          uploads: s.uploads.map((u) =>
            u.videoId === videoId ? { ...u, status, stageLabel: '' } : u,
          ),
        })),
      dismiss: (clientId) =>
        set((s) => ({
          uploads: s.uploads.filter((u) => u.clientId !== clientId),
        })),
      dismissByVideoId: (videoId) =>
        set((s) => ({
          uploads: s.uploads.filter((u) => u.videoId !== videoId),
        })),
      clearAllUploads: () => set({ uploads: [] }),
    }),
    {
      name: 'vqs-upload-store',
      storage: createJSONStorage(() => sessionStorage),
      // On rehydration, convert any interrupted uploads to failed
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        state.uploads = state.uploads.map((u) =>
          u.status === 'uploading' ? { ...u, status: 'failed', stageLabel: '' } : u,
        );
      },
    },
  ),
);

/* ------------------------------------------------------------------ */
/*  UI slice                                                           */
/* ------------------------------------------------------------------ */

interface Notification {
  id: string;
  type: 'success' | 'error' | 'info';
  message: string;
}

interface UiSlice {
  globalLoading: boolean;
  notifications: Notification[];
  setGlobalLoading: (loading: boolean) => void;
  addNotification: (notification: Notification) => void;
  removeNotification: (id: string) => void;
}

export const useUiStore = create<UiSlice>((set) => ({
  globalLoading: false,
  notifications: [],
  setGlobalLoading: (loading) => set({ globalLoading: loading }),
  addNotification: (notification) =>
    set((s) => ({ notifications: [...s.notifications, notification] })),
  removeNotification: (id) =>
    set((s) => ({ notifications: s.notifications.filter((n) => n.id !== id) })),
}));
